import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Users, 
  MapPin, 
  Clock, 
  DollarSign, 
  UserPlus, 
  Edit3, 
  Trash2,
  Eye,
  Plus,
  Filter,
  ChevronDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import SlotCard from './SlotCard';
import CoordinatorAssignDialog from './CoordinatorAssignDialog';
import ExpenseDialog, { ExpenseData, EarningData } from './ExpenseDialog';
import AddExpenseDialog from './AddExpenseDialog';
import ViewSlotDialog from './ViewSlotDialog';
import EditFieldDialog from './EditFieldDialog';
import { apiService } from '../../services/api';
import { 
  InventoryScheduleDocument, 
  SlotFilterData, 
  CoordinatorData, 
  AssignCoordinatorRequest,
  AddExpenseRequest
} from '../../types';
import PDFTestComponent from '../test/PDFTestComponent';

// Use real types from API
type Slot = InventoryScheduleDocument;

interface Coordinator {
  id: string;
  coordinatorId: string;
  coordinatorName: string;
  coordinatorPic: string;
  coordinatorPhoneNumber: string;
  level: number; // 1=Trainee, 2=Main, 3=Head
  confirmed: boolean;
}

interface DateFilter {
  id: number;
  title: string;
  startTime: number;
  endTime: number;
}

const AssignmentPage: React.FC = () => {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [availableCoordinators, setAvailableCoordinators] = useState<CoordinatorData[]>([]);
  const [slotFilters, setSlotFilters] = useState<SlotFilterData[]>([]);
  const [selectedDateFilter, setSelectedDateFilter] = useState<number>(1);
  const [hideCancelled, setHideCancelled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [showCoordinatorDialog, setShowCoordinatorDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showAddExpenseDialog, setShowAddExpenseDialog] = useState(false);
  const [showViewSlotDialog, setShowViewSlotDialog] = useState(false);
  const [showEditFieldDialog, setShowEditFieldDialog] = useState(false);
  
  // Selected data
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [selectedSlotExpenses, setSelectedSlotExpenses] = useState<ExpenseData[]>([]);
  const [selectedSlotEarnings, setSelectedSlotEarnings] = useState<EarningData | undefined>();
  const [assignedCoordinators, setAssignedCoordinators] = useState<Record<string, string[]>>({});
  const [editFieldName, setEditFieldName] = useState('');
  const [editFieldValue, setEditFieldValue] = useState('');
  const [selectedStartTime, setSelectedStartTime] = useState<number | undefined>();
  const [selectedEndTime, setSelectedEndTime] = useState<number | undefined>();

  useEffect(() => {
    loadSlotFilters();
  }, []);

  useEffect(() => {
    if (selectedStartTime && selectedEndTime) {
      loadSlots();
      loadCoordinators();
    }
  }, [selectedStartTime, selectedEndTime, hideCancelled]);

  const loadSlotFilters = async () => {
    try {
      const filters = await apiService.getSlotFilterData();
      setSlotFilters(filters);
      
      // Set default filter to the nearest future date
      const nearestFilter = filters.find(filter => filter.endTime > Date.now());
      if (nearestFilter) {
        setSelectedDateFilter(nearestFilter.id);
        setSelectedStartTime(nearestFilter.startTime);
        setSelectedEndTime(nearestFilter.endTime);
      }
    } catch (error) {
      console.error('Failed to load slot filters:', error);
      // Since filter data is now local, this should not happen
      // But keeping error handling for consistency
      setError('Failed to load date filters');
    }
  };

  const loadSlots = async () => {
    if (!selectedStartTime || !selectedEndTime) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const slotsData = await apiService.getSlots({
        startTime: selectedStartTime,
        endTime: selectedEndTime,
        hideCancelled,
        coordinators: true
      });
      setSlots(slotsData);
    } catch (error) {
      console.error('Failed to load slots:', error);
      setError('Failed to load trip slots');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCoordinators = async () => {
    if (!selectedStartTime || !selectedEndTime) return;
    
    try {
      const coordinators = await apiService.getCoordinatorsInRange({
        startTime: selectedStartTime,
        endTime: selectedEndTime,
        match: false
      });
      setAvailableCoordinators(coordinators);
    } catch (error) {
      console.error('Failed to load coordinators:', error);
    }
  };

  const loadSlotExpenses = async (slotId: string) => {
    try {
      const [expenses, earnings] = await Promise.all([
        apiService.getSlotExpenses(slotId),
        apiService.getSlotEarnings(slotId)
      ]);
      setSelectedSlotExpenses(expenses);
      setSelectedSlotEarnings(earnings);
    } catch (error) {
      console.error('Failed to load slot expenses:', error);
      setSelectedSlotExpenses([]);
      setSelectedSlotEarnings(undefined);
    }
  };

  // Slot actions
  const handleUpdateSlot = async (slotId: string, updates: Partial<Slot>) => {
    try {
      const updatedSlot = await apiService.updateInventorySlot(slotId, updates as UpdateInventorySlotRequest);
      setSlots(prev => prev.map(slot => 
        slot.id === slotId ? updatedSlot : slot
      ));
    } catch (error) {
      console.error('Failed to update slot:', error);
    }
  };

  const handleAssignCoordinator = (slot: Slot) => {
    setSelectedSlot(slot);
    setShowCoordinatorDialog(true);
  };

  const handleViewExpenses = (slot: Slot) => {
    setSelectedSlot(slot);
    loadSlotExpenses(slot.id);
    setShowExpenseDialog(true);
  };

  const handleViewSlotDetails = (slot: Slot) => {
    setSelectedSlot(slot);
    setShowViewSlotDialog(true);
  };

  const handleRemoveCoordinator = async (slotId: string, coordinatorId: string, coordinatorAssociationId: string) => {
    try {
      await apiService.removeCoordinatorFromSlot(slotId, coordinatorId, coordinatorAssociationId);
      // Reload slots to get updated data
      await loadSlots();
    } catch (error) {
      console.error('Failed to remove coordinator:', error);
    }
  };

  const handleConfirmAllCoordinators = async (slotId: string) => {
    try {
      await apiService.confirmAllSlotCoordinators(slotId);
      // Reload slots to get updated data
      await loadSlots();
    } catch (error) {
      console.error('Failed to confirm coordinators:', error);
    }
  };

  // Coordinator dialog actions
  const handleAssignCoordinatorToSlot = async (coordinatorId: string, slotId: string, level: number) => {
    try {
      await apiService.assignCoordinatorToSlot({
        coordinatorId,
        slotId,
        level
      });
      
      // Update assigned coordinators state
      setAssignedCoordinators(prev => ({
        ...prev,
        [slotId]: [...(prev[slotId] || []), coordinatorId]
      }));
      
      // Reload slots to get updated data
      await loadSlots();
    } catch (error) {
      console.error('Failed to assign coordinator:', error);
    }
  };

  const handleCloseCoordinatorDialog = () => {
    setShowCoordinatorDialog(false);
    setSelectedSlot(null);
    setAssignedCoordinators({});
  };

  // Expense dialog actions
  const handleAddExpense = () => {
    setShowAddExpenseDialog(true);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await apiService.deleteExpense(expenseId);
      setSelectedSlotExpenses(prev => prev.filter(expense => expense.id !== expenseId));
    } catch (error) {
      console.error('Failed to delete expense:', error);
    }
  };

  const handleSubmitExpense = async (expenseData: AddExpenseRequest) => {
    try {
      const newExpense = await apiService.addSlotExpense(expenseData);
      setSelectedSlotExpenses(prev => [...prev, newExpense]);
    } catch (error) {
      console.error('Failed to add expense:', error);
    }
  };

  const handleCloseExpenseDialog = () => {
    setShowExpenseDialog(false);
    setShowAddExpenseDialog(false);
    setSelectedSlot(null);
    setSelectedSlotExpenses([]);
    setSelectedSlotEarnings(undefined);
  };

  // View slot dialog actions
  const handleEditField = (fieldName: string, fieldValue: string) => {
    setEditFieldName(fieldName);
    setEditFieldValue(fieldValue);
    setShowEditFieldDialog(true);
  };

  const handleUpdateSlotField = async (slotId: string, fieldName: string, fieldValue: string) => {
    try {
      const updatedSlot = await apiService.updateSlotField(slotId, fieldName, fieldValue);
      setSlots(prev => prev.map(slot => 
        slot.id === slotId ? updatedSlot : slot
      ));
    } catch (error) {
      console.error('Failed to update slot field:', error);
    }
  };

  const handleCloseViewSlotDialog = () => {
    setShowViewSlotDialog(false);
    setSelectedSlot(null);
  };

  const handleCloseEditFieldDialog = () => {
    setShowEditFieldDialog(false);
    setEditFieldName('');
    setEditFieldValue('');
  };

  // Handle date filter change
  const handleDateFilterChange = async (filterId: number) => {
    const filter = slotFilters.find(f => f.id === filterId);
    if (filter) {
      setSelectedDateFilter(filterId);
      setSelectedStartTime(filter.startTime);
      setSelectedEndTime(filter.endTime);
    }
  };

  return (
    <div className="h-full flex bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex-shrink-0">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Hide Cancelled</h3>
            <button
              onClick={() => setHideCancelled(!hideCancelled)}
              className={`p-2 rounded-lg transition-colors ${
                hideCancelled 
                  ? 'bg-primary-100 text-primary-600' 
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {hideCancelled ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            </button>
          </div>
          
          <div className="space-y-2">
            {slotFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => handleDateFilterChange(filter.id)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  selectedDateFilter === filter.id
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="font-medium">{filter.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Trip Assignment</h1>
          <p className="text-gray-600">Manage trip slots and assign coordinators</p>
        </div>

        {/* PDF Test Component with Logo - Remove this after testing */}
        <div className="mb-6">
          <PDFTestComponent />
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                <span className="text-red-800 dark:text-red-200">{error}</span>
              </div>
              <button
                onClick={() => { setError(null); loadSlots(); }}
                className="ml-4 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : slots.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Trips Available</h3>
            <p className="text-gray-600">No trips found for the selected date range.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {slots.map((slot) => (
              <SlotCard
                key={slot.id}
                slot={slot}
                onUpdateSlot={handleUpdateSlot}
                onAssignCoordinator={handleAssignCoordinator}
                onViewExpenses={handleViewExpenses}
                onViewSlotDetails={handleViewSlotDetails}
                onRemoveCoordinator={handleRemoveCoordinator}
                onConfirmAllCoordinators={handleConfirmAllCoordinators}
              />
            ))}
          </div>
        )}
      </div>

      {/* Coordinator Assignment Dialog */}
      {showCoordinatorDialog && selectedSlot && (
        <CoordinatorAssignDialog
          open={showCoordinatorDialog}
          setOpen={setShowCoordinatorDialog}
          slotId={selectedSlot.id}
          startTime={selectedSlot.startTime}
          endTime={selectedSlot.endTime}
          availableCoordinators={availableCoordinators}
          assignedCoordinators={assignedCoordinators}
          onAssignCoordinator={handleAssignCoordinatorToSlot}
          onClose={handleCloseCoordinatorDialog}
        />
      )}

      {/* Expense Dialog */}
      {showExpenseDialog && selectedSlot && (
        <ExpenseDialog
          open={showExpenseDialog}
          setOpen={setShowExpenseDialog}
          slotId={selectedSlot.id}
          slotExpenseData={selectedSlotExpenses}
          slotEarningData={selectedSlotEarnings}
          onAddExpense={handleAddExpense}
          onDeleteExpense={handleDeleteExpense}
          onClose={handleCloseExpenseDialog}
        />
      )}

      {/* Add Expense Dialog */}
      {showAddExpenseDialog && selectedSlot && (
        <AddExpenseDialog
          open={showAddExpenseDialog}
          setOpen={setShowAddExpenseDialog}
          slotId={selectedSlot.id}
          inventoryId={selectedSlot.inventoryId}
          tripTitle={selectedSlot.tripTitle}
          startTime={selectedSlot.startTime}
          endTime={selectedSlot.endTime}
          coordinators={selectedSlot.coordinators || []}
          onClose={handleCloseExpenseDialog}
          onSubmit={handleSubmitExpense}
        />
      )}

      {/* View Slot Dialog */}
      {showViewSlotDialog && selectedSlot && (
        <ViewSlotDialog
          open={showViewSlotDialog}
          setOpen={setShowViewSlotDialog}
          slot={selectedSlot}
          onEditField={handleEditField}
          onClose={handleCloseViewSlotDialog}
        />
      )}

      {/* Edit Field Dialog */}
      {showEditFieldDialog && selectedSlot && (
        <EditFieldDialog
          open={showEditFieldDialog}
          setOpen={setShowEditFieldDialog}
          slot={selectedSlot}
          fieldName={editFieldName}
          fieldValue={editFieldValue}
          onSubmit={handleUpdateSlotField}
          onClose={handleCloseEditFieldDialog}
        />
      )}
    </div>
  );
};

export default AssignmentPage;
